import {
  NavigateFunction, NavigateOptions, To, useLocation, useNavigate,
} from 'react-router-dom';
import { inheritedSearchForTarget, resolvePathname } from '../utils/eventReferral';

/**
 * Next.js App Router patches pushState/replaceState. When the SPA is hosted
 * inside a Next.js layout that conditionally mounts its own BrowserRouter
 * (e.g. the events layout at /events/[slug]/…), a react-router navigate()
 * to an absolute path *outside* that layout's route tree causes Next.js to
 * re-evaluate the layout while the old component tree is still mounted —
 * resulting in a nested-BrowserRouter crash.
 *
 * This function returns the route prefix of such a layout when we're inside
 * one, so we know when a full page load is needed instead.
 */
function getHostingLayoutPrefix(pathname: string): string | null {
  // /events/{slug}/{rest…} is hosted under the events layout which
  // conditionally wraps children in a BrowserRouter.
  const match = pathname.match(/^(\/events\/[^/]+\/)/);
  return match ? match[1] : null;
}

// Mirrors EventsLayoutShell: only /events/{slug}/{rest…} renders without the
// layout's own router, so only those targets are safe for an in-SPA navigate.
function isSpaSubRoute(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean);
  return segments[0] === 'events' && segments.length > 2;
}

/**
 * True when navigating from `currentPathname` to `targetPathname` would make
 * the events layout switch from pass-through to mounting its own router. That
 * includes *relative* targets: `navigate('../../')` from the checkout resolves
 * to /events/{slug}, which is exactly the switch that crashes.
 */
function leavesHostingLayout(currentPathname: string, targetPathname: string): boolean {
  const prefix = getHostingLayoutPrefix(currentPathname);
  if (!prefix || !isSpaSubRoute(currentPathname)) {
    return false;
  }
  return !targetPathname.startsWith(prefix) || !isSpaSubRoute(targetPathname);
}

function hardNavigate(url: string, replace?: boolean) {
  if (replace) {
    window.location.replace(url);
  } else {
    window.location.href = url;
  }
}

const useNavigateWithQuery = (): ((
  path: To,
  options?: NavigateOptions
) => void) => {
  const navigate: NavigateFunction = useNavigate();
  const location = useLocation();

  return (path: To, options?: NavigateOptions) => {
    let fullPath;

    if (typeof path === 'string') {
      const qIndex = path.indexOf('?');
      const targetPathname = resolvePathname(
        location.pathname,
        qIndex === -1 ? path : path.slice(0, qIndex),
      );
      const targetSearch = qIndex === -1 ? '' : path.slice(qIndex);
      const inherited = inheritedSearchForTarget(
        location.pathname,
        location.search,
        targetPathname,
      );
      const inheritedQs = inherited.startsWith('?') ? inherited.slice(1) : inherited;
      const targetQs = targetSearch.startsWith('?') ? targetSearch.slice(1) : targetSearch;
      const mergedQs = [targetQs, inheritedQs].filter(Boolean).join('&');
      const pathnameOnly = qIndex === -1 ? path : path.slice(0, qIndex);
      fullPath = mergedQs ? `${pathnameOnly}?${mergedQs}` : pathnameOnly;

      // Targets that leave the current Next.js layout scope must use a full
      // page load so Next.js handles routing from scratch — no stale component
      // trees, no layout mode switches mid-transition.
      if (leavesHostingLayout(location.pathname, targetPathname)) {
        hardNavigate(mergedQs ? `${targetPathname}?${mergedQs}` : targetPathname, options?.replace);
        return;
      }
    } else {
      const targetPathname = resolvePathname(location.pathname, path.pathname ?? location.pathname);
      const inherited = inheritedSearchForTarget(
        location.pathname,
        location.search,
        targetPathname,
      );
      const inheritedQs = inherited.startsWith('?') ? inherited.slice(1) : inherited;
      let targetQs = '';
      if (path.search) {
        targetQs = path.search.startsWith('?') ? path.search.slice(1) : path.search;
      }
      const mergedQs = [targetQs, inheritedQs].filter(Boolean).join('&');
      fullPath = {
        ...path,
        search: mergedQs ? `?${mergedQs}` : '',
      };

      if (leavesHostingLayout(location.pathname, targetPathname)) {
        hardNavigate(`${targetPathname}${fullPath.search}${path.hash ?? ''}`, options?.replace);
        return;
      }
    }

    navigate(fullPath, options);
  };
};

export default useNavigateWithQuery;
